// Plan 15-07 Task 1 — AddFriendForm.
//
// DEC-002 — three add-a-friend paths: code (text input), link (paste-from-clipboard
// → extract /add/XXX-XXXX), and QR (scan via QRScannerModal). NO username search.
//
// Behavior:
//   - The friend-code regex `^[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{4}$` validates
//     CLIENT-side only for UX hints. The server is authoritative — we send
//     malformed codes anyway and surface the server's 4xx response (oracle-safe
//     404 byte-equal for all friend-not-found / self / already-friends per
//     Phase 7 hardening — see UI-SPEC error-states table). The backend
//     contract uses the bare 7-char code WITHOUT an `RG-` prefix; the
//     `users.friend_code` column and `friends/handlers/request.ts` regex
//     are the source of truth — see CR-01 fix.
//   - Paste-link extracts the friend code from the `/add/XXX-XXXX` URL pattern.
//     If the pasted text does NOT match, we surface an inline hint ("Paste
//     it manually") instead of silently failing.
//   - Scan-QR opens the QRScannerModal; on decoded payload → populate input +
//     close modal. The scanner already validates the payload against the
//     same regex, so anything that reaches `onCodeDetected` is trustworthy.

import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ApiError } from '../../net/client.js'
import { sendFriendRequest } from '../../net/friends.js'
import { Btn } from '../components/Btn'
import { QRScannerModal } from './QRScannerModal.js'

const FRIEND_CODE_RE = /^[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{4}$/
const SHARE_LINK_RE = /\/add\/([A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{4})/

export interface AddFriendFormProps {
  /** Optional spy for tests — invoked after a 200 from sendFriendRequest. */
  readonly onSent?: (request_id: string) => void
}

type FormStatus =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent' }
  | { kind: 'error'; messageId: string; defaultMessage: string }
  | { kind: 'hint'; messageId: string; defaultMessage: string }

function statusFor4xx(status: number): FormStatus {
  // UI-SPEC error states — oracle-safe 404 for all "couldn't add" cases.
  // 400 is malformed-code (server validation).
  if (status === 400) {
    return {
      kind: 'error',
      messageId: 'social.friends.error.malformed_code',
      defaultMessage: "That code doesn't look right. Try again.",
    }
  }
  // Any other 4xx (404 / 409) — generic "couldn't add". Server is
  // oracle-safe; we don't distinguish self / already-friends / not-found.
  return {
    kind: 'error',
    messageId: 'social.friends.error.add_failed',
    defaultMessage: "We couldn't add that friend. Double-check the code.",
  }
}

export function AddFriendForm({ onSent }: AddFriendFormProps): JSX.Element {
  const intl = useIntl()
  const [value, setValue] = useState('')
  const [scannerOpen, setScannerOpen] = useState(false)
  const [status, setStatus] = useState<FormStatus>({ kind: 'idle' })

  const handlePasteLink = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      setStatus({
        kind: 'hint',
        messageId: 'social.friends.paste_link.manual_hint',
        defaultMessage: 'Paste it manually.',
      })
      return
    }
    try {
      const text = await navigator.clipboard.readText()
      const match = SHARE_LINK_RE.exec(text)
      if (match?.[1]) {
        setValue(match[1])
        setStatus({ kind: 'idle' })
      } else {
        setStatus({
          kind: 'hint',
          messageId: 'social.friends.paste_link.manual_hint',
          defaultMessage: 'Paste it manually.',
        })
      }
    } catch {
      // Permission denied or clipboard unsupported — fall back to manual hint.
      setStatus({
        kind: 'hint',
        messageId: 'social.friends.paste_link.manual_hint',
        defaultMessage: 'Paste it manually.',
      })
    }
  }, [])

  const handleScanQR = useCallback(() => {
    setScannerOpen(true)
  }, [])

  const handleCodeDetected = useCallback((code: string) => {
    setValue(code)
    setScannerOpen(false)
    setStatus({ kind: 'idle' })
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (status.kind === 'submitting') return
      const trimmed = value.trim()
      if (trimmed.length === 0) {
        setStatus({
          kind: 'error',
          messageId: 'social.friends.error.empty',
          defaultMessage: 'Type a friend code first.',
        })
        return
      }
      setStatus({ kind: 'submitting' })
      try {
        const res = await sendFriendRequest(trimmed)
        setStatus({ kind: 'sent' })
        // Toast confirmation — Plan 10's i18n table also tracks this key
        // (DOM-event toast vocabulary established by PhaseAPills).
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('runegambit:toast', {
              detail: { id: 'social.friends.request_sent' },
            }),
          )
        }
        setValue('')
        onSent?.(res.request_id)
      } catch (e) {
        if (e instanceof ApiError) {
          setStatus(statusFor4xx(e.status))
        } else {
          setStatus({
            kind: 'error',
            messageId: 'social.friends.error.add_failed',
            defaultMessage:
              "We couldn't add that friend. Double-check the code.",
          })
        }
      }
    },
    [value, status, onSent],
  )

  // Soft pre-validate hint (does NOT block submission — server is authoritative).
  const looksValid = FRIEND_CODE_RE.test(value.trim())
  const submitting = status.kind === 'submitting'

  return (
    <form
      data-testid="add-friend-form"
      onSubmit={(e) => {
        void handleSubmit(e)
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <label
        htmlFor="add-friend-code-input"
        style={{
          fontFamily: 'var(--font-family-body)',
          fontWeight: 500,
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-primary)',
        }}
      >
        <FormattedMessage
          id="social.friends.code_input_label"
          defaultMessage="Enter a friend code"
        />
      </label>
      <input
        id="add-friend-code-input"
        data-testid="add-friend-code-input"
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          if (status.kind === 'error' || status.kind === 'sent') {
            setStatus({ kind: 'idle' })
          }
        }}
        placeholder={intl.formatMessage({
          id: 'social.friends.code_input_placeholder',
          defaultMessage: 'e.g. 7K2-MA4B',
        })}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        disabled={submitting}
        aria-invalid={status.kind === 'error' ? true : undefined}
        style={{
          width: '100%',
          height: '48px',
          padding: '0 var(--space-4)',
          borderRadius: 'var(--radius-pill)',
          // Folk input: borderless near-white field with a warm inset rule.
          border: 'none',
          backgroundColor: 'var(--color-card)',
          boxShadow:
            'inset 0 0 0 2px color-mix(in srgb, var(--color-surface-stroke) 55%, transparent)',
          color: 'var(--color-text-primary)',
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-16)',
          letterSpacing: '0.05em',
          boxSizing: 'border-box',
        }}
      />
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <Btn
          kind="ghost"
          size="md"
          onClick={() => {
            void handlePasteLink()
          }}
          disabled={submitting}
        >
          <FormattedMessage
            id="social.friends.paste_link_cta"
            defaultMessage="Paste link"
          />
        </Btn>
        <Btn
          kind="ghost"
          size="md"
          onClick={handleScanQR}
          disabled={submitting}
        >
          <FormattedMessage
            id="social.friends.scan_qr_cta"
            defaultMessage="Scan QR"
          />
        </Btn>
      </div>
      {(status.kind === 'error' || status.kind === 'hint') && (
        <p
          role={status.kind === 'error' ? 'alert' : 'status'}
          data-testid={
            status.kind === 'error' ? 'add-friend-error' : 'add-friend-hint'
          }
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-13)',
            color:
              status.kind === 'error'
                ? 'var(--color-cta-destructive)'
                : 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id={status.messageId}
            defaultMessage={status.defaultMessage}
          />
        </p>
      )}
      {!looksValid && value.trim().length > 0 && status.kind === 'idle' && (
        <p
          role="status"
          data-testid="add-friend-soft-hint"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-12)',
            color: 'var(--color-text-primary)',
            opacity: 0.7,
          }}
        >
          <FormattedMessage
            id="social.friends.code_input.soft_hint"
            defaultMessage="Codes look like 7K2-MA4B."
          />
        </p>
      )}
      <Btn type="submit" kind="them" size="lg" block disabled={submitting}>
        <FormattedMessage
          id="social.friends.add_by_code"
          defaultMessage="Add a friend"
        />
      </Btn>
      <QRScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onCodeDetected={handleCodeDetected}
      />
    </form>
  )
}
